import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CookieService } from 'ngx-cookie-service';
import { map, mergeMap, catchError, switchMap, withLatestFrom } from 'rxjs/operators';

import * as fromLoginActions from './login.actions';
import { LoginService } from '../login.service';
import { UserService } from '../../../shared/user/user.service';
import { User } from '../../../shared/user/user.model';
import { AppState } from '../../../shared/state/app.reducer';
import { HttpResponse } from '@angular/common/http';
import { AssociationService } from '../../../shared/association/association.service';
import { Association } from '../../../shared/association/association.model';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { UserAccess } from '../../../shared/user/user-access.model';
import { UserAccessService } from '../../../shared/user/user-access.service';
import { RequestTime } from '../../../shared/user/request-time.model';

@Injectable()
export class LoginEffects {

  login$ = createEffect( () =>
    this.actions$.pipe(
      ofType( fromLoginActions.login ),
      mergeMap( ({ email, password }: { email: string, password: string }) =>
        this.authService.login( email, password ),
      ),
      catchError( (err, caught$) => {
        this.toastr.error( this.translate.instant( 'LOGIN.ERROR' ) );
        this.store.dispatch( fromLoginActions.loginFailure() );
        return caught$;
      } ),
      map( (response: HttpResponse<User>) => {
        const token = response.headers.get( 'Authorization' ).split( 'Bearer ' ).pop();
        const id = response.body.id;
        return fromLoginActions.loginSuccess( { token, id } );
      } ),
    ),
  );

  loginSuccess$ = createEffect( () =>
    this.actions$.pipe(
      ofType( fromLoginActions.loginSuccess ),
      map( (logged: { token: string, id: string }) => {
        this.cookieService.set( 'user', logged.id, null, '/' );
        this.cookieService.set( 'idToken', logged.token, null, '/' );
        const redirect = this.route.snapshot.queryParamMap.get( 'redirect' );
        if ( !!redirect ) {
          this.router.navigate( [ redirect ] );
        }
        return fromLoginActions.loadUserAccess( { userId: logged.id } );
      } ),
    ),
  );

  logout$ = createEffect( () =>
    this.actions$.pipe(
      ofType( fromLoginActions.logout ),
      map( () => {
        this.cookieService.delete( 'user', '/' );
        this.cookieService.delete( 'idToken', '/' );
        this.cookieService.delete( 'association', '/' );

        this.router.navigate( [ 'login' ] );
        this.router.dispose();
        return fromLoginActions.logoutSuccess();
      } ),
    ),
  );

  loadUserAccesses$ = createEffect( () =>
    this.actions$.pipe(
      ofType( fromLoginActions.loadUserAccess ),
      switchMap( ({ userId }: { userId: string }) =>
        this.userAccessService.getLoggedInUserAccesses(),
      ),
      catchError( (err, caught$) => {
        this.store.dispatch( fromLoginActions.loadUserAccessFailure() );
        this.toastr.error( err.error.message, this.translate.instant( 'LOGIN.FORBIDDEN' ) );
        this.router.navigate( [ 'login' ] );
        return caught$;
      } ),
      map( (accesses: UserAccess[]) => {
        return fromLoginActions.loadUserAccessSuccess( { accesses } );
      } ),
    ),
  );

  loadLoggedUserSuccess$ = createEffect( () =>
      this.actions$.pipe(
        ofType( fromLoginActions.loadUserAccessSuccess ),
        map( ({ accesses }: { accesses: UserAccess[] }) => {
          const currentAssociation = this.cookieService.get( 'association' );

          const access = this.setAccess( accesses, currentAssociation );

          if ( !!access ) {
            const association = access.association;
            this.cookieService.set( 'association', association, null, '/' );

            const route = this.router.routerState.snapshot;
            if ( !route || route.url === '/login' ) {
              this.router.navigate( [ 'd', association ] );
            }

            this.store.dispatch( fromLoginActions.setUserAccess( { access } ) );
            this.store.dispatch( fromLoginActions.setLoggedUser( { user: accesses[ 0 ].user } ) );
            this.store.dispatch( fromLoginActions.setCurrentAssociation( { associationId: association } ) );
          } else {
            this.router.navigate( [ 'access' ] );
          }
        } ),
      ),
    { dispatch: false },
  );

  getUserAssociations$ = createEffect( () =>
    this.actions$.pipe(
      ofType( fromLoginActions.getUserAssociations ),
      switchMap( () => {
        const user = this.cookieService.get( 'user' );
        return this.associationService.getAll( user );
      } ),
      catchError( (err, caught$) => {
        this.toastr.error( this.translate.instant( 'LOGIN.GET_USER_ASS0CIATION_ERROR' ) );
        this.store.dispatch( fromLoginActions.getUserAssociationsFailure() );
        this.router.parseUrl( 'login' );
        return caught$;
      } ),
      map( (associations: Association[]) => {
        return fromLoginActions.getUserAssociationsSuccess( { associations } );
      } ),
    ),
  );

  setAccess(accesses: UserAccess[], currentAssociation: string): UserAccess{
    if ( accesses.length === 1 ) {
      return accesses[ 0 ];
    } else if ( !!currentAssociation ) {
      return accesses.find( a => a.association === currentAssociation );
    } else {
      return accesses.find( (access) => access.type === 'SUPER' );
    }
  }

  constructor(
    private actions$: Actions,
    private authService: LoginService,
    private associationService: AssociationService,
    private router: Router,
    private route: ActivatedRoute,
    private cookieService: CookieService,
    private userAccessService: UserAccessService,
    private store: Store<AppState>,
    private translate: TranslateService,
    private toastr: ToastrService,
  ){
  }
}
